import { Lts, type RosettaSDK, TinypassSDK, type Tracking } from "@product/rosetta-sdk";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { UntypedGA4Event } from "scmp-app/components/tracking/google-tag-manager/types";

export type RosettaTracker = Tracking.Tracker;
export type RosettaTrackEvent = Tracking.Event;

/**
 * Build a GA4-backed `Tracking.Tracker` that maps Piano/Rosetta subscription
 * events to the app's GA4 tracking pipeline.
 *
 * The event `name` is split on `_` into category / subcategory / action and
 * enriched with the rules-engine `tracking.ga4.baseParameters` outcome plus
 * the LTS bucket, mirroring the abandoned-cart offer dialog implementation.
 *
 * Reused by the soft-paywall integration and the abandoned-cart offer dialog
 * so both flows report identical tracking parameters.
 */
export const createRosettaGA4Tracker = (rosettaSdk: RosettaSDK | undefined): RosettaTracker => ({
  track(event) {
    const segments = event.name.split("_");

    sendGA4Tracking(
      {
        action: segments.at(-1),
        category: segments.at(0),
        customized_parameters: {
          ...(rosettaSdk?.rulesEngine.getOutcome("tracking.ga4.baseParameters" as never) as
            | Record<string, unknown>
            | undefined),
          lts_bucket: Lts.getLtsBucket({
            customVariables: TinypassSDK.loadedInstance?.customVariables,
          }),
          ...event.parameters,
        },
        subcategory: segments.slice(1, -1).join("_"),
      } as unknown as UntypedGA4Event,
      { untyped: true },
    );
  },
});
